/** Workflows: the "Finish setting up" checklist of a workflow (what is left before it runs). Included in `engage.workflows.setup`. */
export const workflowsSetup = {
  title: "Finish setting up",
  subtitle: "Left to check: {{count}}. This list follows your saved draft.",
  allDone: "Everything is set up",
  status: { done: "Done", todo: "To do", warn: "Check" },
  fix: {
    home: "Open Widget Studio Home",
    actions: "Open Actions",
    webhooks: "Open Webhooks",
    team: "Open Team",
    leads: "Open Leads",
    hours: "Set office hours",
    identity: "Open widget security",
    handoff: "Open handoff settings",
  },
  homeSteps: {
    publish: "Publish this workflow with the Publish button above.",
    option: "In Widget Studio, open Home, choose Add option, set the action to Start workflow and pick “{{name}}”.",
    widget: "Publish the widget so visitors see the new option.",
  },
  homeNote: {
    started: "Started by: Home option “{{label}}” on {{agent}}",
    linked: "Linked to Home option “{{label}}” on {{agent}}, but it is not live yet",
    none: "Not on any Home option yet",
    open: "Open Widget Studio Home",
  },
  deliveryModes: { events: "every event it subscribes to", filtered: "the events that match its filters", workflows: "workflow messages only" },
  scope: { allAssistants: "all assistants", allChannels: "all channels" },
  items: {
    publish: {
      never_published: "Publish this workflow. It is still a draft, so visitors and your team never see it run.",
      paused: "This workflow is paused and does not run. Resume it to start again.",
      unpublished_changes: "You have saved changes that are not published yet. Publish to make them live.",
      done: "Published. The live version matches your draft.",
    },
    home_option: {
      no_home_option: "No Home option starts this workflow yet, so it never runs. To connect it:",
      draft_only: "Home option “{{label}}” on {{agent}} points here, but only in the widget draft. Publish the widget to show it to visitors.",
      home_off: "Home option “{{label}}” on {{agent}} points here, but Home is turned off in the published widget. Turn Home on, then publish the widget.",
      agent_excluded: "Home option “{{label}}” on {{agent}} points here, but this workflow is not set to run for {{agent}}. Add that assistant in this workflow's settings below, or put the option on an assistant it covers.",
      done: "Started by Home option “{{label}}” on {{agent}}.",
      done_more: "Started by Home option “{{label}}” on {{agent}} and {{count}} more.",
    },
    booking_action: {
      missing: "A step shows a booking card, but its booking action no longer exists. Pick another one in that step, or create it in Actions.",
      disabled: "Booking action “{{name}}” on {{agent}} is turned off, so the booking card cannot be offered. Turn it on in Actions.",
      done: "Booking action “{{name}}” on {{agent}} is ready.",
    },
    lead_form: {
      missing: "A step shows a lead form, but its lead capture action no longer exists. Pick another one in that step, or create it in Actions.",
      disabled: "Lead capture action “{{name}}” on {{agent}} is turned off, so the lead form cannot be offered. Turn it on in Actions.",
      done: "Lead capture action “{{name}}” on {{agent}} is ready.",
    },
    endpoint: {
      missing: "A Send webhook or Data connector step points at an endpoint that no longer exists. Pick another one in that step, or add it under Webhooks.",
      disabled: "Webhook endpoint “{{name}}” is turned off, so nothing is sent to it. Turn it on under Webhooks.",
      not_workflows_only: "Webhook endpoint “{{name}}” also receives {{mode}}. Under Webhooks, set its delivery to “Only from my workflows” if it should get this workflow's messages only.",
      done: "Webhook endpoint “{{name}}” only receives messages from workflows.",
    },
    team: {
      missing: "A step uses a team that no longer exists. Pick another team in that step, or create one in Team.",
      no_members: "Team “{{name}}” has no active members, so what this workflow sends to it reaches nobody. Add people in Team.",
      done: "Team “{{name}}” is ready (active members: {{count}}).",
    },
    member: {
      missing: "A step names a person who is no longer in this workspace. Pick someone else in that step.",
      not_active: "{{name}} is not an active member, so they will not receive anything from this workflow. Pick someone else, or make them active again in Team.",
      done: "{{name}} is an active member.",
    },
    lead_stage: {
      missing: "A step moves leads to a stage that no longer exists. Pick another stage in that step.",
      done: "Lead stage “{{name}}” exists.",
    },
    handoff_settings: {
      no_default_route: "Handoffs from {{agents}} that match no team go to the Owners and Admins, because no default team or person is set. Choose one under Escalation & handoff in the assistant's settings.",
      done: "Every assistant has a default team or person for handoffs.",
    },
    leads_routing: {
      missing: "Lead routing sends every new lead to one person, but that person is no longer in this workspace, so new leads are shared round-robin instead. Choose another default owner in Leads, Lead routing.",
      not_active: "{{name}} is the default owner of new leads but is not an active member, so new leads are shared round-robin instead. Choose another owner in Leads, Lead routing.",
      cannot_edit_leads: "{{name}} is the default owner of new leads, but their role cannot edit leads for every assistant this workflow runs for, so those leads are shared round-robin instead. Give them access in Team, or choose another owner in Leads, Lead routing.",
      done: "New leads go to {{name}}, the default owner.",
      done_round_robin: "New leads are shared round-robin among the members who can edit leads.",
    },
    endpoints: {
      done_none: "Nothing to send yet: no webhook endpoint takes events. When you add one under Webhooks, this workflow sends it the events it subscribes to.",
      done_one: "Events go to {{count}} webhook endpoint, which gets only the events it subscribes to.",
      done_other: "Events go to {{count}} webhook endpoints, and each gets only the events it subscribes to.",
    },
    office_hours: {
      default_hours: "This workflow follows office hours, but no assistant has its own hours yet. It uses the default week, {{days}} {{from}}–{{to}} ({{timezone}}). Set your hours in the assistant's settings.",
      done: "Office hours are set ({{timezone}}).",
    },
    identity_verification: {
      not_enabled: "This workflow looks for verified visitors, but identity verification is off on the widget, so no visitor counts as verified. Turn it on in the widget's Security tab.",
      done: "Identity verification is on, so verified visitors can be recognised.",
    },
    scope: {
      done: "Runs for: {{assistants}}. Channels: {{channels}}.",
    },
  },
} as const;
